import React, { useEffect, useId, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useMoreState } from "../../lib/moreMemory";

/**
 * MoreSection — issue #1127 (D219). A screen opens simple; this full-width
 * row reveals the rarely-used parts and remembers, per person, that it was
 * opened (`lib/moreMemory.js`). Closed: one "More" row. Open: the same button
 * at the top reads "Less", then the children. No second "Less" at the bottom.
 *
 * `initialOpen` (#1135, D254 Q5): a link that must land on the revealed part
 * (the invite Accept link opens Settings with More already open) opens it for
 * that visit only. Nothing is read or written for it; the person's remembered
 * choice stays as it was, and closing it by hand is an ordinary toggle.
 *
 * Applied to /learn (#1128: `learn-more` folds the session list's filters,
 * `learn-topics-more` "All eight topics" folds the topic list), the record
 * screen (#1129) and Settings (#1135).
 */
export function MoreSection({
  screen,
  userId,
  label = "More",
  lessLabel = "Less",
  initialOpen = false,
  children,
  "data-testid": testId = "more",
}) {
  const [stored, setStored] = useMoreState(screen, userId);
  const [forced, setForced] = useState(Boolean(initialOpen));
  useEffect(() => {
    if (initialOpen) setForced(true);
  }, [initialOpen]);
  const open = stored || forced;
  const setOpen = (next) => {
    if (!next) setForced(false);
    // Opening for this visit alone never touches the store.
    if (next || stored) setStored(next);
  };
  const regionId = useId();
  const Chevron = open ? ChevronUp : ChevronDown;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={testId}
        aria-expanded={open ? "true" : "false"}
        aria-controls={open ? regionId : undefined}
        className="flex items-center justify-between w-full"
        style={{
          minHeight: 50,
          background: "var(--nw-surface)",
          borderRadius: "var(--nw-radius-card)",
          border: "1px solid var(--nw-line)",
          padding: "0 var(--nw-space-card)",
          textAlign: "left",
          cursor: "pointer",
          color: "var(--nw-ink)",
          fontSize: 15,
          fontWeight: 600,
        }}
      >
        <span>{open ? lessLabel : label}</span>
        <Chevron size={18} style={{ color: "var(--nw-ink-faint)", flexShrink: 0 }} aria-hidden="true" />
      </button>
      {open && (
        <div
          id={regionId}
          data-testid={`${testId}-region`}
          style={{ paddingTop: "var(--nw-space-stack)" }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export default MoreSection;
